import React, { useEffect } from "react";
import { ROUTES } from "app-router/routes";
import {
  ADMIN_PERMISSIONS,
  AdminPermission,
} from "helpers/auth/rbac";
import { useAuth } from "context/AuthContext";
import DrawerMenu from "./Drawer";
import useResponsive from "helpers/hooks/useResponsive";
import { ReactComponent as Dashboard } from "assets/icons/home.svg";
import { ReactComponent as Users } from "assets/icons/users.svg";
import { ReactComponent as Supplements } from "assets/icons/supplements.svg";
import { ReactComponent as Scans } from "assets/icons/scanner.svg";
import { ReactComponent as Brands } from "assets/icons/eye.svg";
import { ReactComponent as Categories } from "assets/icons/categories.svg";
import { ReactComponent as ProductTypes } from "assets/icons/product-types.svg";
import { ReactComponent as Banners } from "assets/icons/banners.svg";
import { ReactComponent as Potency } from "assets/icons/potency.svg";
import { ReactComponent as Notification } from "assets/icons/notification.svg";
import { ReactComponent as Coupon } from "assets/icons/coupon.svg";
import { ReactComponent as TestRequests } from "assets/icons/test-request.svg";
import { ReactComponent as PaidTestRequests } from "assets/icons/paid-test-request.svg";
import { ReactComponent as TopRequested } from "assets/icons/top-requested.svg";

type MenuChild = {
  label: string;
  path: string;
  permissions?: AdminPermission[];
};

type MenuItem = {
  label: string;
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  path: string;
  sub_path?: string[];
  permissions?: AdminPermission[];
  children?: MenuChild[];
};

const MENU_ITEMS: MenuItem[] = [
  {
    label: "Dashboard",
    icon: Dashboard,
    path: ROUTES.DASHBOARD,
    permissions: [ADMIN_PERMISSIONS.ANALYTICS_VIEW],
  },
  {
    label: "Admin Users",
    icon: Users,
    path: ROUTES.ADMIN_USERS,
    permissions: [ADMIN_PERMISSIONS.ADMINS_READ],
  },
  {
    label: "Users",
    icon: Users,
    path: ROUTES.USERS,
    sub_path: ["/user-details", "/details"],
    permissions: [ADMIN_PERMISSIONS.USERS_READ],
  },
  {
    label: "Supplements",
    icon: Supplements,
    path: ROUTES.SUPPLEMENTS,
    sub_path: ["/supplement-details"],
    permissions: [ADMIN_PERMISSIONS.SUPPLEMENTS_READ],
  },
  {
    label: "Top 10 Requested",
    icon: TopRequested,
    path: ROUTES.TOP_REQUESTED_SUPPLEMENTS,
    permissions: [ADMIN_PERMISSIONS.TEST_REQUESTS_READ],
  },
  {
    label: "Test Requests",
    icon: TestRequests,
    path: ROUTES.TEST_REQUESTS,
    permissions: [ADMIN_PERMISSIONS.TEST_REQUESTS_READ],
  },
  {
    label: "Paid Test Requests",
    icon: PaidTestRequests,
    path: ROUTES.PAID_TEST_REQUESTS,
    permissions: [ADMIN_PERMISSIONS.TEST_REQUESTS_READ],
  },
  {
    label: "Categories",
    icon: Categories,
    path: ROUTES.CATEGORIES,
    permissions: [ADMIN_PERMISSIONS.CATALOG_READ],
  },
  {
    label: "Brands",
    icon: Brands,
    path: ROUTES.BRANDS,
    permissions: [ADMIN_PERMISSIONS.CATALOG_READ],
  },
  {
    label: "Scans",
    icon: Scans,
    path: ROUTES.SCANS,
    permissions: [ADMIN_PERMISSIONS.SCANS_READ],
  },
  {
    label: "Supplement types",
    icon: ProductTypes,
    path: ROUTES.PRODUCT_TYPES,
    permissions: [ADMIN_PERMISSIONS.CATALOG_READ],
  },
  {
    label: "Banners",
    icon: Banners,
    path: ROUTES.BANNERS,
    permissions: [ADMIN_PERMISSIONS.CONTENT_READ],
    children: [
      {
        label: "Home Page Banners",
        path: ROUTES.BANNERS,
        permissions: [ADMIN_PERMISSIONS.CONTENT_READ],
      },
      {
        label: "Promo Code Banners",
        path: ROUTES.PROMO_BANNERS,
        permissions: [ADMIN_PERMISSIONS.CONTENT_READ],
      },
      {
        label: "Video Banners",
        path: ROUTES.VIDEO_BANNERS,
        permissions: [ADMIN_PERMISSIONS.CONTENT_READ],
      },
    ],
  },
  {
    label: "Manage Potency",
    icon: Potency,
    path: ROUTES.MANAGE_POTENCY,
    permissions: [ADMIN_PERMISSIONS.CATALOG_READ],
  },
  {
    label: "Manage Notifications",
    icon: Notification,
    path: ROUTES.MANAGE_NOTIFICATIONS,
    permissions: [ADMIN_PERMISSIONS.NOTIFICATIONS_WRITE],
  },
  {
    label: "Promo Codes",
    icon: Coupon,
    path: ROUTES.PROMO_CODES,
    permissions: [ADMIN_PERMISSIONS.PROMO_CODES_READ],
  },
];

function Sidebar({ children }: { children: React.ReactNode }) {
  const { canAll } = useAuth();
  const { isDesktop, isLaptop } = useResponsive();
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    if (isDesktop || isLaptop) {
      setOpen(true);
    }
  }, [isDesktop, isLaptop]);

  const toggleDrawer = () => {
    setOpen((prev) => !prev);
  };

  const availableMenuItems = React.useMemo(() => {
    return MENU_ITEMS.reduce<MenuItem[]>((acc, item) => {
      if (!canAll(item.permissions)) {
        return acc;
      }

      const children = item.children?.filter((child) =>
        canAll(child.permissions)
      );

      if (item.children && (!children || children.length === 0)) {
        return acc;
      }

      acc.push({
        ...item,
        children,
      });

      return acc;
    }, []);
  }, [canAll]);

  return (
    <DrawerMenu
      open={open}
      toggleDrawer={toggleDrawer}
      navs={availableMenuItems}
    >
      {children}
    </DrawerMenu>
  );
}

export default Sidebar;
